// SHADOW MARKET™ — Storefront landing aligned with the main REBEL site.
// Same typography (Inter), same orange→amber gradient CTAs, same dark glass.
// Fully translated (ES/EN/SK) via useShopI18n.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Construction, ArrowLeft, Sparkles, ShoppingBag, Flame } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { ShopProductCard } from "../components/ShopProductCard";
import { PRODUCTS, PRODUCT_TYPES, getProductsByType } from "../data/shop-products";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { useLanguage } from "../i18n/LanguageContext";
import { useShopI18n } from "../i18n/shop";
import { useMarketRblI18n } from "../i18n/market-rbl";
import "./shadow-market.css";

const ShadowMarketPage = () => {
  const { lang } = useLanguage();
  const t = useShopI18n(lang);
  const tRbl = useMarketRblI18n(lang);
  const [tab, setTab] = useState("all");
  const { isPro } = useAuth();
  const { openCart } = useCart();
  const list = getProductsByType(tab);

  const TABS = [
    { key: "all", label: t.tab_all },
    { key: "polo", label: t.tab_polo },
    { key: "pantalon", label: t.tab_pantalon },
    { key: "camiseta", label: t.tab_camiseta },
  ];

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Staggered reveal-on-scroll
  useEffect(() => {
    const els = document.querySelectorAll(".shop-reveal");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("in-view")),
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [tab]);

  return (
    <div className="shop-root min-h-screen bg-black text-white antialiased" data-testid="shadow-market-page">
      <Navbar />

      {/* HERO — same aesthetic as the landing */}
      <header
        className="relative overflow-hidden pt-32 pb-12 md:pt-40"
        style={{
          background:
            "radial-gradient(circle at 50% 0%, rgba(255,140,0,0.10), transparent 55%), radial-gradient(circle at 85% 40%, rgba(251,191,36,0.06), transparent 50%)",
        }}
      >
        {/* Subtle grid */}
        <div className="absolute inset-x-0 top-0 mx-auto h-px max-w-5xl bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />

        <div className="mx-auto max-w-6xl px-6 text-center md:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.22em] text-white/40 transition-colors hover:text-orange-300"
            data-testid="shop-back-home"
          >
            <ArrowLeft className="h-3 w-3" /> {t.back_home}
          </Link>

          <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-400/[0.06] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.3em] text-orange-300/90 backdrop-blur-sm">
            <Sparkles className="h-3 w-3" /> {t.eyebrow}
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="shop-h1 mt-6 text-5xl md:text-7xl lg:text-8xl"
          >
            RE
            <span className="bg-gradient-to-br from-orange-300 via-orange-500 to-amber-600 bg-clip-text text-transparent">
              ₿
            </span>
            EL
          </motion.h1>

          <div className="shop-divider-line" />

          <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-white/45">
            {t.tagline}
          </p>

          <div className="mt-6 flex justify-center">
            <span
              className="live-badge inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-400/[0.08] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-200"
            >
              {t.drop_active}
            </span>
          </div>

          {/* UNDER CONSTRUCTION BANNER — same glass-card aesthetic as landing */}
          <div
            className="mx-auto mt-10 flex max-w-2xl items-start gap-3 rounded-2xl border border-orange-400/30 bg-gradient-to-br from-orange-500/[0.06] to-transparent p-5 text-left backdrop-blur-xl"
            data-testid="shop-under-construction"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-orange-400/40 bg-orange-400/10">
              <Construction className="h-4 w-4 text-orange-300" />
            </div>
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.18em] text-orange-300">
                {t.under_construction_title}
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-white/70">
                {t.under_construction_body}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* BANNER R₿L — 25% dto pagando con $R₿L (aditivo, mismo estilo glass) */}
      <section className="px-6 pt-6">
        <div
          className="mx-auto flex max-w-6xl items-center justify-center gap-2.5 rounded-2xl border border-orange-400/40 bg-gradient-to-r from-orange-500/[0.12] via-amber-500/[0.08] to-orange-500/[0.12] px-5 py-3.5 backdrop-blur-xl"
          data-testid="shop-rbl-banner"
        >
          <Flame className="h-4 w-4 shrink-0 text-orange-400" />
          <p className="text-center text-[11px] font-black uppercase tracking-[0.16em] text-orange-200 md:text-xs">
            {tRbl.banner}
          </p>
        </div>
      </section>

      {/* STATS BAR — refined, matches the landing's data strips */}
      <section
        className="border-y border-white/10 bg-gradient-to-b from-white/[0.02] to-transparent backdrop-blur-sm"
      >
        <div className="mx-auto grid max-w-6xl grid-cols-2 sm:grid-cols-4">
          {[
            { label: t.stat_polo, value: "€49.90" },
            { label: t.stat_pantalon, value: "€38.90" },
            { label: t.stat_camiseta, value: "€19.90" },
            { label: t.stat_pro, value: "-15%" },
          ].map((s, i) => (
            <div
              key={s.label + i}
              className={`flex flex-col items-center justify-center py-6 ${
                i < 3 ? "sm:border-r" : ""
              } ${i % 2 === 0 ? "border-r sm:border-r" : ""} border-white/10`}
            >
              <span className="bg-gradient-to-br from-orange-300 to-amber-500 bg-clip-text text-2xl font-black tracking-tight text-transparent">
                {s.value}
              </span>
              <span className="mt-1 text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
                {s.label}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* FILTER TABS — pill style, orange accent for active */}
      <nav
        className="flex flex-wrap justify-center gap-3 px-4 py-8"
        role="tablist"
      >
        {TABS.map((tb) => {
          const active = tab === tb.key;
          return (
            <button
              key={tb.key}
              onClick={() => setTab(tb.key)}
              className={`shop-chip ${active ? "is-active" : ""}`}
              data-testid={`shop-tab-${tb.key}`}
              role="tab"
              aria-selected={active}
            >
              {tb.label}
            </button>
          );
        })}
      </nav>

      {/* PRODUCT GRID */}
      <section className="mx-auto max-w-6xl px-4 pb-12 md:px-6">
        <div
          className="shop-grid-3 grid overflow-hidden rounded-2xl border border-white/10"
          style={{ background: "rgba(255,255,255,0.05)", gap: "1px" }}
        >
          {list.map((p, i) => (
            <div key={p.id} className="shop-reveal" style={{ transitionDelay: `${(i % 3) * 0.08}s` }}>
              <ShopProductCard product={p} indexInRow={i % 3} />
            </div>
          ))}
          {list.length === 0 && (
            <div className="col-span-3 bg-black/40 py-20 text-center text-sm text-white/40">
              {t.card_no_products}
            </div>
          )}
        </div>
      </section>

      {/* PRO upsell — orange CTA, same shape as CtaBand */}
      {!isPro && (
        <section className="relative overflow-hidden border-t border-white/10 px-6 py-14 text-center">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />
          <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-orange-300/80">
            {t.pro_upsell_eyebrow}
          </p>
          <h3 className="mx-auto mt-3 max-w-2xl text-2xl font-black tracking-tight text-white md:text-3xl">
            {t.pro_upsell_title_1}{" "}
            <span className="bg-gradient-to-br from-orange-300 to-amber-600 bg-clip-text text-transparent">
              −15%
            </span>{" "}
            {t.pro_upsell_title_2}
          </h3>
          <Link
            to="/academy"
            className="shop-btn-primary mt-6 hover:no-underline"
            data-testid="shop-pro-upsell"
          >
            {t.pro_upsell_cta} →
          </Link>
        </section>
      )}

      {/* Open cart hint */}
      <div className="px-6 py-8 text-center">
        <button
          onClick={openCart}
          className="shop-btn-ghost"
          data-testid="shop-open-cart-cta"
        >
          <ShoppingBag className="h-3.5 w-3.5" />
          {t.open_cart}
        </button>
      </div>

      <Footer />
    </div>
  );
};

export default ShadowMarketPage;
